import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { BaseMap } from "../../../api/google-maps/base-map";
import { MapsLoaderService } from "../../../services/maps-loader.service";
import { GeoLocationService } from "../../../services/geo-location.service";
import { RegisteredSignal } from "src/app/web-api-client";
import { DarkOverlayMapType } from "src/app/shared/api/google-maps/dark-overlay-map-type";
import { Subscription } from "rxjs";

@Component({
  selector: 'app-map-location-selector',
  templateUrl: './map-location-selector.component.html',
  styleUrls: ['./map-location-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapLocationSelectorComponent extends BaseMap implements OnInit, OnDestroy {
  @ViewChild('map', { static: true }) mapElement: ElementRef<HTMLElement>;
  @ViewChild('topLeftButtonsWrapper') topLeftButtonsWrapper: ElementRef<HTMLDivElement>;

  @Input() readonly: boolean;
  @Input() location: google.maps.LatLngLiteral;
  @Input() zoom: number = 5;
  @Input() focusView: boolean = true;

  @Output() locationUpdated = new EventEmitter<google.maps.LatLngLiteral>();

  public mapLoaded = false;
  public registeredSignal: RegisteredSignal = null;

  private incidentMarker: google.maps.marker.AdvancedMarkerElement;
  private infowindow: google.maps.InfoWindow;
  private infoWindowOpened: boolean;
  private subscriptions = new Subscription();

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly mapsLoaderService: MapsLoaderService,
    protected readonly geoLocationService: GeoLocationService
  ) {
    super(mapsLoaderService, geoLocationService);
  }

  ngOnInit(): void {
    this.initMap();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  protected initMap(): void {
    super.initMap(this.mapElement, null);

    if(this.focusView) {
      this.map.overlayMapTypes.insertAt(0, new DarkOverlayMapType(new google.maps.Size(256, 256)));
    }

    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.onMapLoaded();
      this.mapLoaded = true;
      this.changeDetectorRef.detectChanges();

      if(!this.readonly) {
        this.initControlsOverlay();
      }
    });
  }

  public onAutocompleteResult(place: google.maps.places.PlaceResult): void {
    if (place) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      this.updateIncidentLocation({
        lat: lat,
        lng: lng
      });
    }
  }

  public onMyLocationAvailable(latLng: google.maps.LatLngLiteral) {
    if(latLng) {
      this.updateIncidentLocation(latLng);
      this.map.setZoom(15);
    }
  }

  private onMapLoaded() {
    this.map.setZoom(this.zoom);
    if(this.location) {
      this.initIncidentMarker(this.location);
    } else {
      super.initMyLocation((myLocation: google.maps.LatLngLiteral) => {
        if(myLocation) {
          this.map.setCenter(myLocation);
        } else {
          const centerLocation = <google.maps.LatLngLiteral>this.mapsLoaderService.mapConfiguration.default_location;
          this.map.setCenter(centerLocation);
        }
      });
    }

    this.registerMapEvents();
  }

  private initControlsOverlay() {
    this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(this.topLeftButtonsWrapper.nativeElement);
  }

  private initIncidentMarker(location: google.maps.LatLngLiteral, centerMap: boolean = true) {
    if(centerMap) {
      this.map.panTo(location);
    }

    const markerWrapper: HTMLDivElement = document.createElement('div');
    markerWrapper.classList.add('object-marker-wrapper');
    markerWrapper.innerHTML = `*`;

    this.incidentMarker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: location,
      gmpDraggable: !this.readonly,
      //content: markerWrapper
    });
    this.incidentMarker.addListener('dragstart', (ev) => {
      this.infowindow.close();
      this.infowindow.setContent('...');
      this.infoWindowOpened = false;
    });
    this.incidentMarker.addListener('dragend', (ev) => {
      this.updateIncidentLocation(ev.latLng.toJSON());
    });
    this.incidentMarker.addListener('click', (ev) => {
      if(!this.infoWindowOpened) {
        this.infoWindowOpened = true;
        this.infowindow.open({
          anchor: this.incidentMarker,
          map: this.map,
        });
        this.updateInfoWindow();
      }
    });

    this.infowindow = new google.maps.InfoWindow({
      content: 'Търсене на адрес...',
      maxWidth: 300
    });
    this.infowindow.addListener('closeclick', () => {
      this.infoWindowOpened = false;
    });
  }

  private registerMapEvents() {
    this.map.addListener('click', (ev: google.maps.MapMouseEvent) => {
      if(!this.readonly) {
        this.updateIncidentLocation(ev.latLng.toJSON());
      }
    });

    // google.maps.event.addListenerOnce(this.map, 'click', (ev: google.maps.MapMouseEvent) => {
    //   const flag = !this.readonly && !this.incidentMarker;
    //   if(flag) {
    //     this.updateIncidentLocation(ev.latLng.toJSON());
    //   }
    // });
  }

  private updateIncidentLocation(latLng: google.maps.LatLngLiteral) {
    if(!this.incidentMarker) {
      this.initIncidentMarker(latLng);
    }

    this.incidentMarker.position = latLng;
    this.map.panTo(latLng);

    if(this.infoWindowOpened) {
      this.updateInfoWindow();
    }

    this.locationUpdated.emit(latLng);
  }

  private updateInfoWindow() {
    if(!this.incidentMarker || !this.infowindow) {
      return;
    }

    const location = this.incidentMarker.position as google.maps.LatLngLiteral;

    this.subscriptions.add(
      this.geoLocationService.getAddressFromCoordinates(location.lat, location.lng).subscribe(results => {
        if(results.length) {
          const formattedAddress = results[0].formatted_address;
          this.infowindow.setContent(formattedAddress);
        }
      })
    );
  }
}
